import * as R from 'ramda';
import useLocalStorage from '@rehooks/local-storage';
import { useCallback, useMemo } from 'react';
import { BasicProfile } from '.';
import useTechSkills from '../../../hooks/useTechSkills';
import TechnicalSkill from '../../../models/technical-skills';

type TechnicalSkillSummary = Pick<TechnicalSkill, 'id' | 'name'>;

type BasicProfileStored = Omit<BasicProfile, 'skills'> & {
  skills?: TechnicalSkillSummary[];
};

function mapToFullSkills(skills: TechnicalSkillSummary[], fullSkills?: TechnicalSkill[]) {
  if (!fullSkills || !skills) return [];
  const mapped = R.map((sk: TechnicalSkillSummary) => R.find<TechnicalSkill>(R.propEq('id', sk.id))(fullSkills))(skills);
  return R.filter(Boolean, mapped) as TechnicalSkill[];
}

function mapToSummarySkills(fullSkills?: TechnicalSkill[]): TechnicalSkillSummary[] | undefined {
  if (!fullSkills) return undefined;
  return R.map(R.pick(['id', 'name']), fullSkills);
}

export default function useBasicProfileStorage() {
  const { fetchingSkills, skills: fullSkills } = useTechSkills();
  const [basicProfile, writeBasicProfileToLS] = useLocalStorage<BasicProfileStored>('basicProfile');
  const writeBasicProfile = useCallback(
    (profile: BasicProfile) => writeBasicProfileToLS({
      firstName: profile.firstName,
      experience: profile.experience,
      skills: mapToSummarySkills(profile.skills),
    }),
    [writeBasicProfileToLS],
  );
  const basicProfileWithDefaults = useMemo(
    () => ({
      firstName: basicProfile?.firstName ?? '',
      experience: basicProfile?.experience ?? '',
      skills: mapToFullSkills(
        basicProfile?.skills ?? [],
        fullSkills,
      ),
    } as Required<BasicProfile>),
    [basicProfile, fullSkills],
  );
  return {
    basicProfile: basicProfileWithDefaults,
    writeBasicProfile,
    fetchingBasicProfile: fetchingSkills,
  };
}
